export const marketplaceGuides = [
  {
    slug: "blueprint-protocol-v4",
    name: "Blueprint Protocol v4",
    creator: "Blueprint Lab",
    creatorBadge: "Verified Creator",
    creatorBio:
      "Dedicated to extending healthy human lifespan through rigorous measurement and data-driven protocols.",
    category: "Longevity",
    icon: "∞",
    tone: "bg-kin-green/12 text-kin-green-dark",
    description:
      "The definitive longevity AI configuration. Optimized for biological age reduction and healthspan extension.",
    about:
      "This guide leverages data from thousands of clinical measurements to provide a comprehensive, personalized plan for slowing and reversing biological aging.",
    details:
      "It directs your vault to analyze biomarkers, sleep patterns, and activity data to generate daily recommendations for diet, supplements, and lifestyle.",
    rating: 4.9,
    installs: "10k+",
    duration: "3 min run",
    requiredData: ["Blood panels", "Wearable sleep", "Activity", "Supplements log"],
    actions: [
      {
        title: "Track Biological Age",
        text: "Monitor your biological age score over time with clinical-grade context.",
        color: "bg-kin-green/14 text-kin-green-dark",
      },
      {
        title: "Optimize Diet and Supplements",
        text: "Receive precise, data-driven intake recommendations.",
        color: "bg-kin-gold/20 text-kin-body",
      },
      {
        title: "Analyze Biomarkers",
        text: "Deep dive into 50+ key indicators from uploaded lab panels.",
        color: "bg-kin-lavender/20 text-kin-dark",
      },
      {
        title: "Improve Sleep Quality",
        text: "Get actionable sleep protocols based on your own nightly trends.",
        color: "bg-kin-orange/18 text-kin-dark",
      },
    ],
    previewCards: [
      { label: "Biological Age Report", value: "26.4 years", spark: [16, 18, 21, 23, 25, 26] },
      { label: "CRP (Inflammation)", value: "0.4 mg/L", bars: [30, 46, 62, 55, 40, 50, 45] },
      {
        label: "Daily Routine Checklist",
        value: "4 of 5 complete",
        checklist: [true, true, false, true, true],
      },
    ],
    reviews: [
      {
        quote: "Life-changing. My biological age is down 3 years.",
        author: "Alex R.",
      },
      {
        quote: "Complex data made simple into a daily plan.",
        author: "Sarah K.",
      },
    ],
    featured: true,
  },
  {
    slug: "metabolic-flexibility-guide",
    name: "Metabolic Flexibility Guide",
    creator: "Metabolic Institute",
    creatorBadge: "Verified Creator",
    creatorBio:
      "Clinical-first metabolic optimization for sustained energy and insulin resilience.",
    category: "Metabolic Health",
    icon: "◍",
    tone: "bg-kin-orange/20 text-kin-dark",
    description: "Optimize fuel usage for energy and long-term metabolic performance.",
    about:
      "This protocol analyzes glucose variability, meal timing, and movement patterns to determine which routines stabilize your energy curve.",
    details:
      "You receive meal sequencing, timing windows, and recovery triggers personalized to your daily response patterns.",
    rating: 4.8,
    installs: "8k+",
    duration: "2 min run",
    requiredData: ["CGM", "Meals", "Activity"],
    actions: [
      {
        title: "Reduce Glucose Volatility",
        text: "Pinpoint meal contexts that create spikes and crashes.",
        color: "bg-kin-gold/20 text-kin-body",
      },
      {
        title: "Improve Energy Stability",
        text: "Get timing recommendations for activity and nutrition.",
        color: "bg-kin-green/14 text-kin-green-dark",
      },
    ],
    previewCards: [
      { label: "Glucose In Range", value: "74%", bars: [42, 55, 68, 60, 47, 62, 58] },
      { label: "Post-Meal Peaks", value: "-18%", spark: [24, 22, 21, 19, 18, 17] },
      { label: "Routine Adherence", value: "91%", checklist: [true, true, true, false, true] },
    ],
    reviews: [
      { quote: "My afternoon crashes basically disappeared.", author: "Chris M." },
      { quote: "Clear protocol with immediate impact.", author: "Jamie L." },
    ],
    featured: true,
  },
  {
    slug: "sleep-recovery-stack",
    name: "Sleep and Recovery Stack",
    creator: "Recovery Collective",
    creatorBadge: "Verified Creator",
    creatorBio: "A team of clinicians and performance coaches focused on restoration science.",
    category: "Sleep and Recovery",
    icon: "◔",
    tone: "bg-kin-lavender/18 text-kin-dark",
    description: "Community favorite for deep rest, recovery, and nervous system balance.",
    about:
      "This guide combines bedtime consistency, recovery markers, and daily load to tune your sleep architecture.",
    details:
      "You get a practical schedule with personalized sleep windows, wind-down sequence, and weekly recovery pacing.",
    rating: 4.7,
    installs: "6k+",
    duration: "2 min run",
    requiredData: ["Wearable sleep", "Resting HR", "Daily load"],
    actions: [
      {
        title: "Stabilize Bedtime",
        text: "Set your ideal range to improve recovery consistency.",
        color: "bg-kin-lavender/22 text-kin-dark",
      },
      {
        title: "Reduce Sleep Fragmentation",
        text: "Get concrete adjustments for caffeine, light, and meal timing.",
        color: "bg-kin-gold/18 text-kin-body",
      },
    ],
    previewCards: [
      { label: "Avg Sleep", value: "7.1 h", spark: [61, 63, 64, 66, 67, 69] },
      { label: "Recovery Score", value: "82", bars: [35, 48, 52, 58, 64, 70, 74] },
      { label: "Wind-down Adherence", value: "4 of 5", checklist: [true, false, true, true, true] },
    ],
    reviews: [
      { quote: "Best sleep structure I have ever had.", author: "Morgan T." },
      { quote: "Finally predictable recovery days.", author: "Dana P." },
    ],
    featured: true,
  },
  {
    slug: "hormone-balance-protocol",
    name: "Hormone Balance Protocol",
    creator: "Dr. Nina Larsen",
    creatorBadge: "Verified Creator",
    creatorBio: "Specialized in endocrinology-informed female and male hormone optimization.",
    category: "Hormones",
    icon: "△",
    tone: "bg-kin-green/10 text-kin-green-dark",
    description: "Cycle-aware hormone interpretation with practical follow-up actions.",
    about: "Correlates hormone markers with symptom timelines and recovery context.",
    details: "Highlights mismatch windows and recommends targeted tracking routines.",
    rating: 4.7,
    installs: "7k+",
    duration: "4 min run",
    requiredData: ["Labs", "Symptoms", "Cycle history"],
    actions: [
      {
        title: "Map Hormonal Patterns",
        text: "Understand where patterns diverge from your baseline.",
        color: "bg-kin-lavender/20 text-kin-dark",
      },
    ],
    previewCards: [
      { label: "Pattern Confidence", value: "High", bars: [36, 44, 48, 54, 63, 60, 68] },
      { label: "Action Items", value: "5", checklist: [true, true, false, true, false] },
    ],
    reviews: [
      { quote: "Finally made my labs understandable.", author: "Elena K." },
      { quote: "Very actionable between visits.", author: "Maja S." },
    ],
  },
  {
    slug: "cognitive-clarity-guide",
    name: "Cognitive Clarity Guide",
    creator: "NeuroLab Group",
    creatorBadge: "Verified Creator",
    creatorBio: "Research-backed protocols for attention, focus, and mental resilience.",
    category: "Cognitive Boost",
    icon: "◌",
    tone: "bg-kin-lavender/18 text-kin-dark",
    description: "Improve focus stability and reduce cognitive drift across your week.",
    about: "Combines sleep debt, workload, and nutrition timing to score cognitive readiness.",
    details: "Delivers practical adjustments that support deep-work consistency.",
    rating: 4.6,
    installs: "5k+",
    duration: "3 min run",
    requiredData: ["Sleep", "Routine logs", "Nutrition"],
    actions: [
      {
        title: "Increase Deep-Work Windows",
        text: "Align focus blocks with energy and sleep signals.",
        color: "bg-kin-gold/18 text-kin-body",
      },
    ],
    previewCards: [
      { label: "Focus Readiness", value: "78", spark: [58, 61, 66, 69, 73, 78] },
      { label: "Drift Events", value: "-21%", bars: [66, 59, 54, 48, 43, 39, 35] },
    ],
    reviews: [
      { quote: "My workdays feel way less chaotic.", author: "Tobias L." },
      { quote: "Clear and measurable guidance.", author: "Hana V." },
    ],
  },
  {
    slug: "cardio-health-stack",
    name: "Cardio Health Stack",
    creator: "Cardio Research Hub",
    creatorBadge: "Verified Creator",
    creatorBio: "Preventive cardiology team focused on trend-driven risk reduction.",
    category: "Cardio Health",
    icon: "♡",
    tone: "bg-kin-orange/20 text-kin-dark",
    description: "Longitudinal cardio trend tracking with prevention-focused protocols.",
    about: "Uses resting heart rate, HRV, blood markers, and activity load to estimate resilience.",
    details: "Surfaces realistic interventions and when to check in with your clinician.",
    rating: 4.6,
    installs: "4k+",
    duration: "3 min run",
    requiredData: ["HRV", "Resting HR", "Labs", "Activity"],
    actions: [
      {
        title: "Track Recovery Pressure",
        text: "Detect persistent stress load earlier.",
        color: "bg-kin-green/14 text-kin-green-dark",
      },
    ],
    previewCards: [
      { label: "Resilience Index", value: "71", spark: [52, 56, 58, 63, 67, 71] },
      { label: "RHR Drift", value: "-3 bpm", bars: [70, 66, 62, 59, 56, 54, 52] },
    ],
    reviews: [
      { quote: "Great for understanding trend shifts early.", author: "Jon P." },
      { quote: "Makes preventive cardio practical.", author: "Marta R." },
    ],
  },
  {
    slug: "immune-support-guide",
    name: "Immune Support Guide",
    creator: "Immunity Studio",
    creatorBadge: "Verified Creator",
    creatorBio: "Evidence-based protocols for resilience and recovery seasonality.",
    category: "Immune Support",
    icon: "✚",
    tone: "bg-kin-gold/18 text-kin-body",
    description: "Build personalized immune support habits based on your baseline markers.",
    about: "Tracks sleep, stress, and key blood markers for early warning patterns.",
    details: "Prioritizes low-friction interventions for daily immune resilience.",
    rating: 4.5,
    installs: "4k+",
    duration: "2 min run",
    requiredData: ["Sleep", "Stress logs", "Labs"],
    actions: [
      {
        title: "Strengthen Daily Baseline",
        text: "Get consistency-focused routines that protect recovery capacity.",
        color: "bg-kin-lavender/18 text-kin-dark",
      },
    ],
    previewCards: [
      { label: "Resilience Score", value: "76", spark: [62, 64, 67, 70, 73, 76] },
      { label: "Warning Signals", value: "2", checklist: [true, false, true, false, true] },
    ],
    reviews: [
      { quote: "Helpful before I burn out.", author: "Rina M." },
      { quote: "Simple and actually sustainable.", author: "Leo B." },
    ],
  },
];

export const categoryCounts = [
  { name: "Gut Health", count: 12, icon: "∿", tone: "bg-kin-gold/35 text-kin-body" },
  { name: "Longevity", count: 14, icon: "∞", tone: "bg-kin-lavender/28 text-kin-dark" },
  { name: "Hormone Balance", count: 12, icon: "△", tone: "bg-kin-green/24 text-kin-green-dark" },
  { name: "Cognitive Boost", count: 13, icon: "◌", tone: "bg-kin-lavender/35 text-kin-dark" },
  { name: "Cardio Health", count: 18, icon: "♡", tone: "bg-kin-orange/32 text-kin-dark" },
  { name: "Immune Support", count: 12, icon: "✚", tone: "bg-kin-gold/26 text-kin-body" },
];

export function getGuideBySlug(slug) {
  return marketplaceGuides.find((guide) => guide.slug === slug);
}
